// Customer Relations > Monthly Report — Rachael's end-of-month recap from CPx data, sent from her
// own mailbox. Replaces the spreadsheets one section at a time (Greg, Oct 27, 2026). The data and
// the email are built server side (netlify/functions/lib/cr-monthly-report.js), so what she sees
// here and what Greg and Matt get are the same numbers.

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Send, Paperclip, X, Eye } from 'lucide-react';
import { neonQuery } from '../services/api';

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const money = (v) => {
  const s = '$' + Math.abs(n(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n(v) < 0 ? `(${s})` : s;
};
const day = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 15).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};
const COMPANY = { CP: 'Crab Place', CC: 'Crab & Cruise' };
// Still attached by hand until each has a home in CPx.
const STILL_ATTACHED = ['Credits Issued', 'CR Sales', 'Max Agent Data', 'UPS Report (disputes, delays)'];

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top', fontSize: 13 };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const sub = { fontSize: 12, color: 'var(--gray-600)' };

function Notes({ month, section, value, onSaved }) {
  const [text, setText] = useState(value || '');
  const [state, setState] = useState('');
  useEffect(() => { setText(value || ''); }, [value, month]);
  const save = async () => {
    if ((value || '') === text) return;
    setState('Saving…');
    try {
      await neonQuery('cr_monthly_note_save', { month, section, body: text });
      setState('Saved');
      onSaved(section, text);
    } catch (e) {
      setState(`Not saved: ${e.message}`);
    }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <textarea
        className="form-input" rows={2} style={{ width: '100%', fontFamily: 'inherit' }} value={text}
        placeholder="Notes for Greg and Matt (optional). Saved as you go." onChange={(e) => setText(e.target.value)} onBlur={save}
        aria-label={`${section} notes`}
      />
      {state ? <div style={sub}>{state}</div> : null}
    </div>
  );
}

const Card = ({ title, extra, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-header" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{title}</span>{extra}
    </div>
    <div className="card-body">{children}</div>
  </div>
);

export default function CrMonthlyReport() {
  const [month, setMonth] = useState(lastMonth());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [to, setTo] = useState('');
  const [files, setFiles] = useState([]);        // [{ name, size, contentBytes }]
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    setSent('');
    try {
      const r = await neonQuery('cr_monthly_report', { month });
      setD(r);
      setTo((t) => t || r.defaultTo);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const noteSaved = (section, body) => setD((x) => ({ ...x, notes: { ...x.notes, [section]: body } }));

  const setReason = async (row, reason, note) => {
    setD((x) => ({ ...x, address: { ...x.address, rows: x.address.rows.map((r) => (r.tracking_number === row.tracking_number ? { ...r, reason, note } : r)) } }));
    try {
      await neonQuery('cr_address_reason_save', { tracking: row.tracking_number, reason, note });
    } catch (e) {
      setErr(`Could not save the reason: ${e.message}`);
    }
  };

  const addFiles = async (list) => {
    const read = (f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve({ name: f.name, size: f.size, contentBytes: String(r.result).split(',')[1] });
      r.onerror = () => reject(r.error);
      r.readAsDataURL(f);
    });
    const got = await Promise.all(Array.from(list).map(read));
    setFiles((cur) => [...cur.filter((c) => !got.some((g) => g.name === c.name)), ...got]);
  };

  const send = async () => {
    if (!window.confirm(`Send the ${d.monthName} report to ${to}${files.length ? ` with ${files.length} attachment${files.length === 1 ? '' : 's'}` : ''}?`)) return;
    setSending(true);
    setErr('');
    try {
      const r = await neonQuery('cr_monthly_report_send', { month, to, attachments: files.map(({ name, contentBytes }) => ({ name, contentBytes })) });
      setSent(`Sent from ${r.from} to ${r.to.join(', ')}. It's in your Sent Items.`);
      setFiles([]);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  const cb = d && d.chargebacks;
  const ad = d && d.address;
  const fb = d && d.feedback;
  const cl = d && d.claims;

  return (
    <div>
      <div className="breadcrumb"><span>Customer Relations</span><span> / </span><span>Monthly Report</span></div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Monthly Report</h1>
          <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
            Your end-of-month recap for Greg and Matt, built from CPx. Add notes, attach whatever isn't in CPx yet, and send.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="cr-month" style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-700)', marginBottom: 4 }}>Month</label>
            <input id="cr-month" type="month" className="form-input" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </div>
          <button type="button" className="btn btn-outline" onClick={load} disabled={loading}><RefreshCw size={14} style={{ marginRight: 6 }} />{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {err ? <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)' }}><div className="card-body" style={{ color: 'var(--orange)' }}>{err}</div></div> : null}
      {!d ? (loading ? <div style={{ padding: 24, color: 'var(--gray-600)' }}>Loading…</div> : null) : (
        <>
          <Card title={`${d.monthName}: summary`}>
            <div style={sub}>A line or two at the top of the email, like the notes you put in the body now.</div>
            <Notes month={month} section="summary" value={d.notes.summary} onSaved={noteSaved} />
          </Card>

          {/* ── Chargebacks ── */}
          <Card title="Chargebacks" extra={<span style={sub}>· from the Chargebacks log, received this month</span>}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Company', 'Chargebacks', 'Losses', 'Credits received', 'Net', 'Not a loss'].map((h, i) => <th key={h} style={{ ...th, ...(i ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr></thead>
              <tbody>
                {cb.totals.length ? cb.totals.map((t) => (
                  <tr key={t.company}>
                    <td style={{ ...td, fontWeight: 600 }}>{COMPANY[t.company] || t.company}</td>
                    <td style={{ ...td, ...num }}>{t.chargebacks}</td>
                    <td style={{ ...td, ...num }}>{money(t.losses)}</td>
                    <td style={{ ...td, ...num }}>{money(t.credits)}</td>
                    <td style={{ ...td, ...num, fontWeight: 700, color: n(t.net) >= 0 ? '#15803d' : '#b00020' }}>{money(t.net)}</td>
                    <td style={{ ...td, ...num }}>{t.excluded_vip_count} VIP · {t.excluded_resold_count} resold</td>
                  </tr>
                )) : <tr><td style={td} colSpan={6}>None received this month.</td></tr>}
              </tbody>
            </table>
            {cb.rows.length ? (
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Received', 'Customer', 'Order', 'Case type', 'Category', 'Company', 'Debit', 'Credit', 'Status'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {cb.rows.map((c) => (
                      <tr key={c.id}>
                        <td style={td}>{day(c.received)}</td><td style={td}>{c.customer}</td><td style={td}>{c.order || ''}</td>
                        <td style={td}>{c.case_type || c.reason || ''}</td><td style={td}>{c.category || <span style={{ color: '#b45309' }}>none</span>}</td>
                        <td style={td}>{COMPANY[c.company] || c.company}</td>
                        <td style={{ ...td, ...num }}>{money(c.debited)}</td><td style={{ ...td, ...num }}>{money(c.credited)}</td><td style={td}>{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div style={{ ...sub, marginTop: 8 }}>Fix anything wrong on the Chargebacks page; this report reads the same log.</div>
            <Notes month={month} section="chargebacks" value={d.notes.chargebacks} onSaved={noteSaved} />
          </Card>

          {/* ── Address corrections ── */}
          <Card title="Address corrections" extra={<span style={sub}>· from the UPS bills, by billed week</span>}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>
              <b>{ad.rows.length}</b> correction{ad.rows.length === 1 ? '' : 's'}, <b>{money(ad.total)}</b>
              {ad.cpErrorTotal ? <>, of which <b>{money(ad.cpErrorTotal)}</b> was a Crab Place error</> : null}.
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
              {ad.weeks.map((w) => (
                <div key={w.invoice_number} style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                  <b>Billed {day(w.billed)}</b><div>{w.package_count} packages · {w.corrections.length ? `${w.corrections.length} corrected` : <span style={{ color: '#15803d' }}>Great job!</span>}</div>
                </div>
              ))}
            </div>
            {ad.rows.length ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Billed', 'Order', 'Ship date', 'Charge', 'Who took it', 'Reason', 'Note'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {ad.rows.map((r) => (
                      <tr key={r.tracking_number}>
                        <td style={td}>{day(r.billed)}</td>
                        <td style={td}>{r.bc_order_id || r.order_ref}<div style={sub}>{r.tracking_number}</div></td>
                        <td style={td}>{day(r.ship_date)}</td>
                        <td style={{ ...td, ...num }}>{money(r.charge)}</td>
                        <td style={td}>{r.taken_by || 'Placed online'}</td>
                        <td style={td}>
                          <select className="form-select" value={r.reason || ''} onChange={(e) => setReason(r, e.target.value, r.note || '')} aria-label="Reason for the correction"
                            style={{ borderColor: r.reason ? undefined : '#f59e0b' }}>
                            <option value="">Pick a reason…</option>
                            {d.reasons.map((x) => <option key={x} value={x}>{x}</option>)}
                          </select>
                        </td>
                        <td style={td}>
                          <input className="form-input" style={{ width: 180 }} defaultValue={r.note || ''} placeholder="Who verified, how"
                            onBlur={(e) => { if (e.target.value !== (r.note || '')) setReason(r, r.reason || '', e.target.value); }} aria-label="Note" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {ad.ytd.length ? (
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <div style={{ ...sub, fontWeight: 600, marginBottom: 4 }}>The year so far</div>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th} />{ad.ytd.map((m) => <th key={m.month} style={{ ...th, textAlign: 'right' }}>{monthLabel(m.month)}</th>)}</tr></thead>
                  <tbody>
                    <tr><td style={td}>Packages billed</td>{ad.ytd.map((m) => <td key={m.month} style={{ ...td, ...num }}>{m.packages.toLocaleString()}</td>)}</tr>
                    <tr><td style={td}>Corrections</td>{ad.ytd.map((m) => <td key={m.month} style={{ ...td, ...num }}>{m.corrections}</td>)}</tr>
                    <tr><td style={td}>Cost</td>{ad.ytd.map((m) => <td key={m.month} style={{ ...td, ...num }}>{money(m.cost)}</td>)}</tr>
                    <tr><td style={td}>Crab Place error</td>{ad.ytd.map((m) => <td key={m.month} style={{ ...td, ...num }}>{money(m.cpErrorCost)}{m.unsorted ? <div style={{ ...sub, color: '#b45309' }}>{m.unsorted} not sorted</div> : null}</td>)}</tr>
                  </tbody>
                </table>
              </div>
            ) : null}
            <Notes month={month} section="address" value={d.notes.address} onSaved={noteSaved} />
          </Card>

          {/* ── Feedback ── */}
          <Card title="Customer feedback" extra={<span style={sub}>· survey answers received this month</span>}>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>{['Responses', 'Product', 'Packaging', 'Delivery', 'CR', 'Ease', 'Order again', 'Overall'].map((h) => <th key={h} style={{ ...th, textAlign: 'right' }}>{h}</th>)}</tr></thead>
              <tbody><tr>
                {['responses', 'product', 'packaging', 'delivery', 'cr', 'ease', 'order_again', 'overall'].map((k) => (
                  <td key={k} style={{ ...td, ...num, fontWeight: 600 }}>{fb.month[k] == null ? '' : k === 'responses' ? fb.month[k] : n(fb.month[k]).toFixed(2)}</td>
                ))}
              </tr></tbody>
            </table>
            <div style={{ ...sub, marginTop: 6 }}>
              A question the customer skipped is left out of the average. The admin portal's feedback report counts it as a 0, so these run a little higher than that report.
            </div>
            <Notes month={month} section="feedback" value={d.notes.feedback} onSaved={noteSaved} />
          </Card>

          {/* ── UPS claims ── */}
          <Card title="UPS claims in CPx" extra={<span style={sub}>· filed this month</span>}>
            {cl.rows.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Filed', 'Order', 'Customer', 'Claim', 'Status', 'Claimed', 'Paid'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {cl.rows.map((c) => (
                    <tr key={c.claim_number}>
                      <td style={td}>{day(c.filed)}</td><td style={td}>{c.bc_order_id}</td><td style={td}>{c.customer_name}</td><td style={td}>{c.claim_number}</td>
                      <td style={td}>{c.status}</td><td style={{ ...td, ...num }}>{money(c.claimed)}</td><td style={{ ...td, ...num }}>{c.paid != null ? money(c.paid) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div style={{ fontSize: 13 }}>No claims filed through CPx this month.</div>}
            <div style={{ fontSize: 13, marginTop: 6 }}>Resolved this month: {cl.resolved.n} ({money(cl.resolved.paid)} paid, {cl.resolved.denied} denied).</div>
            {cl.lastFiled && cl.lastFiled < d.from ? (
              <div style={{ ...sub, color: '#b45309', marginTop: 6 }}>
                The last claim filed through CPx was {day(cl.lastFiled)}, so claims filed on the UPS site aren't here. Keep attaching your UPS Report for now.
              </div>
            ) : null}
            <Notes month={month} section="claims" value={d.notes.claims} onSaved={noteSaved} />
          </Card>

          {/* ── Send ── */}
          <Card title="Send it" extra={<span style={sub}>· from your Outlook, so it's in your Sent Items</span>}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 320px' }}>
                <label htmlFor="cr-to" style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-700)', marginBottom: 4 }}>To</label>
                <input id="cr-to" className="form-input" style={{ width: '100%' }} value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <label className="btn btn-outline" style={{ cursor: 'pointer' }}>
                <Paperclip size={14} style={{ marginRight: 6 }} />Attach files
                <input type="file" multiple style={{ display: 'none' }} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              </label>
              <button type="button" className="btn btn-outline" onClick={() => { if (!preview) load(); setPreview((p) => !p); }}><Eye size={14} style={{ marginRight: 6 }} />{preview ? 'Hide preview' : 'Preview the email'}</button>
              <button type="button" className="btn btn-primary" onClick={send} disabled={sending || !to}><Send size={14} style={{ marginRight: 6 }} />{sending ? 'Sending…' : 'Send'}</button>
            </div>
            <div style={{ ...sub, marginTop: 8 }}>Still attach until they're in CPx: {STILL_ATTACHED.join(', ')}.</div>
            {files.length ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {files.map((f) => (
                  <span key={f.name} style={{ border: '1px solid var(--gray-200)', borderRadius: 12, padding: '2px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {f.name} <span style={sub}>{Math.round(f.size / 1024)} KB</span>
                    <button type="button" className="btn btn-ghost" style={{ padding: 0 }} onClick={() => setFiles((cur) => cur.filter((c) => c.name !== f.name))} aria-label={`Remove ${f.name}`}><X size={12} /></button>
                  </span>
                ))}
              </div>
            ) : null}
            {sent ? <div style={{ marginTop: 10, color: '#15803d', fontWeight: 600 }}>{sent}</div> : null}
            {d.sends && d.sends.length ? (
              <div style={{ ...sub, marginTop: 8 }}>
                Sent before: {d.sends.map((s) => `${new Date(s.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} by ${s.sent_by} to ${s.sent_to}`).join('; ')}
              </div>
            ) : null}
            {preview ? (
              <iframe title="Email preview" srcDoc={d.html} style={{ width: '100%', height: 600, border: '1px solid var(--gray-200)', borderRadius: 6, marginTop: 12, background: 'white' }} />
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}
